import { useState, type AnchorHTMLAttributes, type MouseEvent } from 'react';
import { useTranslations } from '@/i18n/translations';
import { Button, Dialog, DialogFooter } from '@leanin/ui';
import { hasLinkHrefScheme } from '@leanin/shared/url/web-scheme';
import { RouterLinkAs } from '@/lib/router-link';
import { internalPath, isSameDocumentRef } from './internal-url';

// The single renderer for user- and member-entered links (design 2026-08-06
// Project B). It splits three cases:
//
//   - Same-document references (a `#fragment`, `?query`-only, or document-
//     relative href) render as a native anchor. They are same-origin by
//     definition, so the browser resolves them against the current page — a
//     `#`-fragment scrolls to that heading (TOC links, footnotes) with no
//     interstitial and no new tab.
//   - Internal Connect destinations (root-relative paths, same-origin, or a
//     connect.leanin.org host) navigate in the SAME tab via the client-side
//     router — no `target="_blank"`, no interstitial — so members stay in
//     their session instead of accumulating browser tabs.
//   - Everything else keeps the raw external anchor. Cross-origin http(s)
//     (the leanin.org marketing site, …) gates navigation behind a confirm
//     dialog; mailto:/tel: native-handler schemes pass straight through. Any
//     other scheme (javascript:, data:, …) is refused and rendered as inert
//     text — it is never emitted as a live href.
//
// The internal/external split is decided from the href string at render time
// (window origin is consulted only when present), so rendering stays
// render-to-string safe. The confirm dialog mounts only after a gated click,
// keeping plain body rendering free of the translations hook. Auxclick
// (middle-click) on an external link is not interceptable here and falls back
// to the anchor's own noopener/noreferrer attributes.
type ExternalLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  readonly href: string;
};


export function ExternalLink({ href, children, ...rest }: ExternalLinkProps) {
  const [pendingUrl, setPendingUrl] = useState<URL | null>(null);

  if (isSameDocumentRef(href)) {
    // rehype never stamps target/rel on same-document refs, but strip them
    // defensively so a native in-page anchor can never inherit new-tab behaviour.
    const { target: _target, rel: _rel, ...anchorRest } = rest;
    return (
      <a href={href} {...anchorRest}>
        {children}
      </a>
    );
  }

  const to = internalPath(href, typeof window !== 'undefined' ? window.location.origin : null);
  if (to !== null) {
    // Forward only the LinkAsProps-shaped subset: RouterLinkAs's contract
    // deliberately does not model target/rel (rehype stamps target="_blank" on
    // absolute connect.leanin.org links — carrying it onto the SPA link would
    // reopen the new-tab bug this component prevents) or pointer handlers.
    return (
      <RouterLinkAs href={to} className={rest.className} style={rest.style} title={rest.title}>
        {children}
      </RouterLinkAs>
    );
  }

  // Off-site link. Anything that isn't a safe link scheme (javascript:, data:,
  // vbscript:, file:, …) renders as inert text — never a live href, regardless
  // of what the caller passed. A backstop for member-entered hrefs (social
  // links, event links) that don't pass through the markdown sanitizer, and
  // for values stored before the server boundary refused those schemes.
  if (
    !hasLinkHrefScheme(
      href,
      typeof window !== 'undefined' ? window.location.href : 'https://connect.leanin.org',
    )
  ) {
    return <>{children}</>;
  }

  const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    rest.onClick?.(event);
    if (event.defaultPrevented) return;
    let url: URL;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
    event.preventDefault();
    setPendingUrl(url);
  };

  // Wrappers that forward their own props (`Button` / `Pill` with
  // `linkAs={ExternalLink}`) pass `target={undefined}` when unset; an explicit
  // undefined must not erase the off-site new-tab default.
  const { target, rel, ...anchorRest } = rest;
  return (
    <>
      <a
        href={href}
        {...anchorRest}
        target={target ?? '_blank'}
        rel={rel ?? 'noopener noreferrer'}
        onClick={handleClick}
      >
        {children}
      </a>
      {pendingUrl !== null ? (
        <ExternalLinkDialog url={pendingUrl} onClose={() => setPendingUrl(null)} />
      ) : null}
    </>
  );
}

// Mounted only after a gated click, so the translations hook never runs during
// plain body rendering (keeps render-to-string paths free of IntlProvider).
function ExternalLinkDialog({
  url,
  onClose,
}: {
  readonly url: URL;
  readonly onClose: () => void;
}) {
  const t = useTranslations('Common.externalLink');
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('title')}
      description={t('description', { host: url.host })}
      size="sm"
    >
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            window.open(url.href, '_blank', 'noopener,noreferrer');
            onClose();
          }}
        >
          {t('continue')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
